import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import {
  Typography, Box, Paper, Button, TextField,
  IconButton, Stack, Chip, Card, CardContent,
  Tooltip, InputAdornment, Alert, CircularProgress,
  Grid, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  fetchItemCategories,
  createItemCategory,
  updateItemCategory,
  deleteItemCategory,
} from '../slices/storeSlice';
import { useEffect } from 'react';

const MotionPaper = motion(Paper);

const StorePage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { itemCategories, categoriesLoading, categoriesSaving } =
    useSelector((state) => state.store);

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });

  useEffect(() => {
    dispatch(fetchItemCategories());
  }, [dispatch]);

  const filtered = useMemo(() =>
    (itemCategories || []).filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(search.toLowerCase())
    ), [itemCategories, search]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, description: cat.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('❌ يرجى إدخال اسم النوع'); return; }
    try {
      if (editing) {
        await dispatch(updateItemCategory({ id: editing.id, ...form })).unwrap();
        showToast('✅ تم تحديث النوع');
      } else {
        await dispatch(createItemCategory(form)).unwrap();
        showToast('✅ تم إضافة النوع');
      }
      setDialogOpen(false);
    } catch (e) {
      const msg = e?.name?.[0] || e?.detail || 'فشل في الحفظ';
      showToast(`❌ ${msg}`);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`حذف النوع "${cat.name}"؟`)) return;
    try {
      await dispatch(deleteItemCategory(cat.id)).unwrap();
      showToast('✅ تم الحذف');
    } catch { showToast('❌ فشل الحذف'); }
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CategoryIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إدارة المستودع
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                عرّف أنواع الأصناف (أقلام، ألوان، فرش…) — ستظهر تلقائياً عند تسجيل المبيعات والمشتريات
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة نوع
          </Button>
        </MotionPaper>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        >
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
                  color: '#fff',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h3" fontWeight={800} sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        {(itemCategories || []).length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        إجمالي الأنواع
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderRadius: 2.5,
                        width: 56,
                        height: 56,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CategoryIcon sx={{ fontSize: 32, color: '#fff' }} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Alert
                severity="info"
                sx={{
                  height: '100%',
                  alignItems: 'center',
                  borderRadius: 3,
                  bgcolor: '#E0F2FE',
                  color: '#1E1B4B',
                  border: '1px solid #E5E7EB',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '& .MuiAlert-icon': { color: '#0284C7' },
                }}
              >
                <strong>كيفية الاستخدام:</strong> أضف أنواع الأصناف هنا (مثل: أقلام، ألوان، فرش، دفاتر، حقائب…).
                عند الذهاب إلى <strong>مبيعات المدرسة</strong> لتسجيل بيع أو شراء، ستجد هذه الأنواع جاهزة للاختيار منها.
              </Alert>
            </Grid>
          </Grid>

          {/* Toolbar */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              mb: 3,
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                fullWidth label="بحث في الأنواع" value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#6B7280' }} /></InputAdornment> }}
                placeholder="ابحث عن نوع..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#4338CA' },
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& .MuiInputLabel-root': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                }}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
                sx={{
                  whiteSpace: 'nowrap',
                  bgcolor: '#4338CA',
                  color: '#fff',
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  px: 4,
                  py: 1.4,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                }}>
                إضافة نوع
              </Button>
            </Stack>
          </MotionPaper>
        </motion.div>

        {/* List */}
        {categoriesLoading ? (
          <Box sx={{ textAlign: 'center', p: 6 }}><CircularProgress size={50} sx={{ color: '#4338CA' }} /></Box>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
              }}
            >
              <CategoryIcon sx={{ fontSize: 72, color: '#6B7280', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                {search ? 'لا توجد أنواع مطابقة' : 'لا توجد أنواع بعد'}
              </Typography>
              {!search && (
                <>
                  <Typography variant="body2" mb={3} sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    ابدأ بإضافة أنواع مثل: أقلام، ألوان، فرش، دفاتر…
                  </Typography>
                  <Stack useFlexGap direction="row" spacing={1} justifyContent="center" flexWrap="wrap" gap={1}>
                    {['أقلام', 'ألوان', 'فرش', 'دفاتر', 'ورق', 'حقائب', 'أدوات', 'أخرى'].map(s => (
                      <Chip key={s} label={`+ ${s}`} variant="outlined" clickable
                        onClick={() => { setForm({ name: s, description: '' }); setEditing(null); setDialogOpen(true); }}
                        sx={{
                          borderColor: '#4338CA',
                          color: '#4338CA',
                          fontWeight: 700,
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          '&:hover': { bgcolor: '#EEF2FF' },
                        }} />
                    ))}
                  </Stack>
                </>
              )}
            </Paper>
          </motion.div>
        ) : (
          <Grid container spacing={2}>
            <AnimatePresence>
              {filtered.map((cat, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }} transition={{ delay: idx * 0.04 }}>
                    <Card elevation={0} sx={{
                      borderRadius: 3,
                      bgcolor: '#fff',
                      borderTop: '4px solid #4338CA',
                      border: '1px solid #E5E7EB',
                      borderTopWidth: '4px',
                      borderTopColor: '#4338CA',
                      boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(67,56,202,0.15)' }
                    }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={0.5}>
                              <Box
                                sx={{
                                  bgcolor: '#EEF2FF',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <CategoryIcon sx={{ color: '#4338CA', fontSize: 20 }} />
                              </Box>
                              <Typography variant="h6" fontWeight={700} noWrap sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {cat.name}
                              </Typography>
                            </Stack>
                            {cat.description && (
                              <Typography variant="body2" sx={{ mt: 0.5, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                                {cat.description}
                              </Typography>
                            )}
                          </Box>
                          <Stack useFlexGap direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                            <Tooltip title="تعديل">
                              <IconButton
                                size="small"
                                onClick={() => openEdit(cat)}
                                sx={{ color: '#D97706', '&:hover': { bgcolor: '#FEF3C7' } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(cat)}
                                sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}

        {/* Add / Edit dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              direction: 'rtl',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            },
          }}
        >
          <DialogTitle
            sx={{
              background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              color: '#fff',
              fontWeight: 800,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {editing ? <EditIcon /> : <AddIcon />}
            {editing ? 'تعديل النوع' : 'إضافة نوع جديد'}
          </DialogTitle>
          <DialogContent sx={{ mt: 1 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField fullWidth label="اسم النوع" value={form.name} required
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="مثال: أقلام، ألوان، فرش، دفاتر..."
                helperText="سيظهر هذا الاسم كخيار في صفحة المبيعات والمشتريات"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&:hover fieldset': { borderColor: '#4338CA' },
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& .MuiInputLabel-root': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                }} />
              <TextField fullWidth label="وصف (اختياري)" multiline rows={2}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="وصف مختصر..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&:hover fieldset': { borderColor: '#4338CA' },
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& .MuiInputLabel-root': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                }} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDialogOpen(false)}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إلغاء
            </Button>
            <Button variant="contained" onClick={handleSave}
              disabled={categoriesSaving || !form.name.trim()}
              sx={{
                bgcolor: '#4338CA',
                color: '#fff',
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                px: 3,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}>
              {categoriesSaving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default StorePage;
